import { TopicApi, TopicCourseGroupApi, TopicTrainierApi, topicsApi } from "api";
import { useCourseGroupsOptions, useTopics, useTrainersOptions } from "api/hooks";
import {
  AdminResourceDeleteModal,
  AdminSearchInput,
  FiltersBackground,
  PageHeader,
  PageMain,
} from "components/molecules";
import { routes } from "consts";
import { COURSE_SCHEDULE_TYPE_OPTIONS, COURSE_TYPEID_NAMES } from "consts/courseScheduleType";
import { IDropdownOption } from "components/atoms";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteResourceActions } from "utils/store";
import { MultiSelect, PaziaButton } from "components/atoms";
import {
  TanTable,
  TanTableColumn,
  useFilters,
  usePagination,
  useSorting,
} from "components/molecules";
import { formatDate, minutesToTime } from "utils";

export const TopicsPage = (): JSX.Element | null => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { searchPhrase, filter, setSearchPhrase, changeFilter, getInitFilterValues } = useFilters("topics");
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);

  const { isLoading, isFetching, topics, total, invalidate } = useTopics(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const { isLoading: loadingTrainers, options: trainersOptions } = useTrainersOptions();
  const { loading: loadingCourseGroups, options: courseGroupsOptions } = useCourseGroupsOptions();

  const navigate = useNavigate();

  const onSearchChange = (searchPhrase: string) => setSearchPhrase(searchPhrase);

  const onTrainersChange = (trainersOptions: IDropdownOption[]) => {
    if (trainersOptions.length < 1) return changeFilter("trainers", "");
    const trainersIds = trainersOptions.map((x) => `'${x.value}'`).join(",");
    changeFilter("trainers", `trainers in (${trainersIds})`);
  };

  const onTypesChange = (types: IDropdownOption[]) => {
    if (types.length < 1) return changeFilter("types", "");
    const typesIds = types.map((x) => `'${x.value}'`).join(",");
    changeFilter("types", `types in (${typesIds})`);
  };

  const onCourseGroupChange = (courseGroups: IDropdownOption[]) => {
    if (courseGroups.length < 1) return changeFilter("courseGroups", "");
    const courseGroupsIds = courseGroups.map((x) => `'${x.value}'`).join(",");
    changeFilter("courseGroups", `courseGroups in (${courseGroupsIds})`);
  };

  const columns = React.useMemo<TanTableColumn<TopicApi>[]>(() => cols, []);

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Jesteś pewien że chcesz usunąć ${selectedTopics.length} tematów?`)) {
      return;
    }
    try {
      await Promise.all(selectedTopics.map((id) => topicsApi.delete(id)));
      setSelectedTopics([]);
      invalidate();
    } catch (error) {
      console.error("Failed to delete topics:", error);
    }
  };

  return (
    <PageMain>
      <PageHeader
        button={{
          title: "Dodaj +",
          onClick: () => navigate(routes.TOPICS_ADD),
        }}
      >
        Tematy
      </PageHeader>

      <FiltersBackground>
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput onSearch={onSearchChange} className="self-end" />
          <MultiSelect
            label="Trenerzy"
            name="trainers"
            defaultValue={getInitFilterValues("trainers")}
            isLoading={loadingTrainers}
            options={trainersOptions}
            onChange={onTrainersChange}
            className="grow"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelect
            label="Typ"
            name="types"
            options={COURSE_SCHEDULE_TYPE_OPTIONS}
            defaultValue={getInitFilterValues("types")}
            onChange={onTypesChange}
            className="w-full sm:w-auto sm:min-w-[22rem]"
          />
          <MultiSelect
            label="Grupa kursów"
            name="courseGroups"
            isLoading={loadingCourseGroups}
            defaultValue={getInitFilterValues("courseGroups")}
            options={courseGroupsOptions}
            onChange={onCourseGroupChange}
            className="grow"
          />
        </div>
      </FiltersBackground>

      <div className="my-4 flex justify-end">
        <PaziaButton type="danger" disabled={selectedTopics.length < 1} onClick={handleDeleteSelected}>
          Usuń zaznaczone ({selectedTopics.length})
        </PaziaButton>
      </div>

      <TanTable
        data={topics}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        pagination={pagination}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.TOPICS_EDIT(x.id))}
        // @ts-ignore TODO ASDSA
        onSelectionChange={setSelectedTopics}
      />

      <AdminResourceDeleteModal
        title="Usuń temat"
        description="Jesteś pewien że chcesz usunąć temat?"
        onSuccessDelete={() => invalidate()}
        deleteAction={topicsApi.delete}
      />
    </PageMain>
  );
};

const cols: TanTableColumn<TopicApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
  },
  {
    id: "title",
    accessorKey: "title",
    header: "Tytuł",
    size: 500,
    enableColumnFilter: false,
  },
  {
    id: "type",
    accessorKey: "type",
    header: "Typ",
    size: 50,
    enableColumnFilter: false,
    cell: (info) => COURSE_TYPEID_NAMES[info.getValue<1 | 2 | 3>()],
  },
  {
    id: "time",
    hideOnMobile: true,
    accessorKey: "time",
    header: "Czas",
    size: 40,
    enableColumnFilter: false,
    cell: (info) => minutesToTime(info.getValue<number>()) || <span className="flex justify-center">-</span>,
  },
  {
    id: "courseGroups",
    hideOnMobile: true,
    accessorKey: "courseGroups",
    header: "Grupa kursów",
    enableColumnFilter: false,
    size: 300,
    cell: (info) => {
      const groups = info.getValue<TopicCourseGroupApi[]>();
      return (
        <span>
          {groups.map((x) => x.name)} <br />
        </span>
      );
    },
  },
  {
    id: "trainers",
    hideOnMobile: true,
    accessorKey: "trainers",
    header: "Trenerzy",
    enableColumnFilter: false,
    size: 300,
    cell: (info) => {
      const trainers = info.getValue<TopicTrainierApi[]>();
      return trainers.map((x) => (
        <>
          <span>{`${x.firstName} ${x.surname}`}</span> <br />
        </>
      ));
    },
  },
  {
    id: "createdDate",
    hideOnMobile: true,
    accessorKey: "createdDate",
    header: "Data utworzenia",
    size: 200,
    cell: (info) => formatDate(info.getValue<Date>(), true, true),
    enableColumnFilter: false,
  },
];
